import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt"; // Ensure ReceiptIcon is imported
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import MedicineOrderEdit from "./PosEdit";
import { useTranslation } from "react-i18next";
import MedicineOrderPrint from "./MedicineOrderPrint";
import Print from "../CollectPrescriptionFees/Print"; // Import for receipts

const MedicineOrderList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Pagination state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1); // One-based page index
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0);

  const { handleDelete: handleDeleteOrder, ConfirmDialog } = useDelete(
    "medicine-orders",
    token,
  );
  const {
    handleDelete: handleDeleteOrderItem,
    ConfirmDialog: ConfirmDialogItem,
  } = useDelete("medicine-order-items", token);

  // Fetch medicine orders with pagination
  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObjects(
      ["medicine-orders"],
      `medicine-orders/?search=${encodeURIComponent(
        searchQuery,
      )}&page=${page}&page_size=${rowsPerPage}`,
      token,
    );

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPrescriptions(data.count);
    }
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const printRef = useRef();
  const receiptRef = useRef();

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handlePrintClick = (order) => {
    setPrintData(order);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const handleReceiptClick = (order) => {
    setReceiptData(order);
    setTimeout(() => {
      if (receiptRef.current) {
        const receiptWindow = window.open("", "_blank");
        receiptWindow.document.write(receiptRef.current.innerHTML);
        receiptWindow.document.close();
        receiptWindow.focus();
        receiptWindow.print();
        receiptWindow.close();
      }
    }, 500);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 1:
        return t("pharmacy.pending");
      case 2:
        return t("pharmacy.paid");
      case 3:
        return t("pharmacy.canceled");
      default:
        return t("pharmacy.unknown");
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 1:
        return "warning";
      case 2:
        return "success";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6" color="error">
          {t("pharmacy.errorLoadingOrders")}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => refetch()}>
          {t("pharmacy.retry")}
        </Button>
      </Box>
    );
  }

  if (isSuccess) {
    const prescriptions = data?.results || [];

    return (
      <Grid container spacing={3} mt={3} paddingX={isSmallScreen ? 2 : 3}>
        {/* Top Section: Title and Add New Button */}
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isSmallScreen ? "stretch" : "center"}
            mb={3}
            gap={isSmallScreen ? 2 : 0}
          >
            {/* Title */}
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary"
              sx={{ flex: isSmallScreen ? "none" : "1 1 auto" }}
            >
              {t("pharmacy.medicineOrderList")}
            </Typography>

            {/* Add New Button */}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/pharmacy/pos/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("pharmacy.addNew")}
            </Button>
          </Box>
        </Grid>

        {/* Search Bar */}
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Grid>

        {/* Existing Medicine Orders */}
        {prescriptions.length > 0 ? (
          prescriptions.map((order) => {
            const isPaid = order.payment_status === 2;
            const isLoan = order.is_loan;

            return (
              <Grid item xs={12} md={6} key={order.id}>
                <Paper
                  elevation={isLoan ? 4 : isPaid ? 2 : 4}
                  sx={{
                    mb: 3,
                    padding: "24px",
                    borderRadius: "12px",
                    width: "100%",
                    boxShadow: isLoan
                      ? "0px 8px 24px rgba(0, 123, 255, 0.3)"
                      : isPaid
                        ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                        : "0px 8px 24px rgba(255, 0, 0, 0.3)",
                    backgroundColor: isLoan
                      ? "#e3f2fd"
                      : isPaid
                        ? "#f9f9f9"
                        : "#ffebee",
                    borderLeft: isLoan
                      ? "4px solid #1976d2"
                      : isPaid
                        ? "none"
                        : "4px solid #d32f2f",
                  }}
                >
                  {/* Header: Order Info and Actions */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="primary"
                        sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                      >
                        {t("pharmacy.order")} #{order.id} - {order.date}
                      </Typography>
                      {order.customer_name && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          <strong>{t("pharmacy.customerName")}:</strong>{" "}
                          {order.customer_name}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={getPaymentStatusLabel(order.payment_status)}
                        color={getPaymentStatusColor(order.payment_status)}
                        sx={{
                          fontWeight: "bold",
                          fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                        }}
                      />
                      <Tooltip title={t("pharmacy.printOrder")}>
                        <IconButton
                          color="primary"
                          onClick={() => handlePrintClick(order)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("pharmacy.printReceipt")}>
                        <IconButton
                          color="secondary"
                          onClick={() => handleReceiptClick(order)}
                        >
                          <ReceiptIcon /> {/* Use ReceiptIcon for receipts */}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Loan Information */}
                  {isLoan && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: "#bbdefb" }}>
                      <Typography variant="body1">
                        <strong>{t("pharmacy.name")}: </strong>
                        {order.name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>{t("pharmacy.fatherName")}: </strong>
                        {order.father_name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>{t("pharmacy.phoneNumber")}: </strong>
                        {order.phone_number}
                      </Typography>
                    </Box>
                  )}

                  {/* Total Price */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                  >
                    <Typography
                      fontSize={isSmallScreen ? "0.85rem" : "1rem"}
                      color={
                        isLoan
                          ? "info.main"
                          : !isPaid
                            ? "error.main"
                            : "text.primary"
                      }
                    >
                      <strong>{t("pharmacy.total_price")}:</strong>{" "}
                      {order.total_price} ؋
                    </Typography>
                  </Stack>

                  {/* Delete Order Button */}
                  {!isPaid && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <Tooltip title={t("pharmacy.deleteOrder")}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteOrder(order.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  {/* Accordion: Medicine Order Details */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${order.id}-content`}
                      id={`panel-${order.id}-header`}
                    >
                      <Typography
                        fontWeight="bold"
                        color={!isPaid ? "error.main" : "text.primary"}
                      >
                        {t("pharmacy.medicineOrder")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {order.items && order.items.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">#</TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.category")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.medicine")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.quantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.pricePerQuantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.subtotal")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.action")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {order.items.map((item, index) => (
                                <TableRow
                                  key={item.id}
                                  sx={{
                                    "&:hover": { backgroundColor: "#f0f4f8" },
                                  }}
                                >
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {index + 1}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.stock_medicine_category_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.stock_medicine_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>{item.quantity}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      {item.sell_price_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      {item.sell_price_display * item.quantity}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {!isPaid && (
                                      <>
                                        <Tooltip title={t("pharmacy.editItem")}>
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleOpen(item)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t("pharmacy.deleteItem")}
                                        >
                                          <IconButton
                                            color="error"
                                            onClick={() =>
                                              handleDeleteOrderItem(item.id)
                                            }
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>{t("pharmacy.noMedicineOrder")}</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box textAlign="center" mt={5}>
              <Typography variant="h6">
                {t("pharmacy.noMedicineOrder")}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("pharmacy.rowsPerPage")}: </Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPrescriptions / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        {/* Dialogs and Hidden Print Sections */}
        <ConfirmDialog />
        <ConfirmDialogItem />

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            <Typography fontWeight="bold" color="primary">
              {t("pharmacy.editOrderItem")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "20px" }}>
            {selectedItem && (
              <MedicineOrderEdit
                medicineOrderItem={selectedItem}
                onClose={handleClose}
                token={token}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Print Section (Hidden) */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <MedicineOrderPrint data={printData} />
          </div>
        )}

        {/* Receipt Section (Hidden) */}
        {receiptData && (
          <div ref={receiptRef} style={{ display: "none" }}>
            <Print data={receiptData} />{" "}
            {/* Use the correct component for receipts */}
          </div>
        )}
      </Grid>
    );
  }
};

export default MedicineOrderList;
